import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { BYPASS_MODE } from '@/config/demoRole'

declare module 'axios' {
  interface AxiosRequestConfig {
    /** La pagina dice gia' l'errore da se': niente toast. `true` per ogni
     *  status, o la lista degli status attesi (es. `[404]` per "non esiste"). */
    silenzioso?: boolean | number[]
  }
}

/** Il toast d'errore: mai per un 401 (si va al login), mai per uno status che
 *  il chiamante ha dichiarato di mostrare da se'. */
export const mostraToast = (status: number, silenzioso?: boolean | number[]): boolean =>
  status !== 401 && silenzioso !== true && !(Array.isArray(silenzioso) && silenzioso.includes(status))

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'
const API_TOKEN = import.meta.env.VITE_API_TOKEN // Static token for service-to-service calls

// Custom error class for API errors
export class ApiError extends Error {
  status: number
  detail: string
  code?: string
  /** Il corpo di `detail` quando il backend manda un oggetto invece di una
   *  stringa (es. `{ message, articoli_non_prezzati: [...] }`): chi ha
   *  bisogno dei campi oltre al messaggio lo legge da qui. */
  raw?: Record<string, unknown>

  constructor(status: number, detail: string, code?: string, raw?: Record<string, unknown>) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.code = code
    this.raw = raw
  }
}

// Extract error message from API response
export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string | Record<string, unknown>; message?: string; error?: string }>

    // Check for validation errors
    if (axiosError.response?.data) {
      const data = axiosError.response.data
      if (typeof data === 'string') return data
      if (data.detail) {
        if (typeof data.detail === 'string') return data.detail
        // Il backend manda un oggetto quando c'e' altro oltre al messaggio
        // (es. la lista degli articoli senza prezzo): il messaggio resta qui,
        // il resto lo legge chi vuole i dettagli da `ApiError.raw`.
        const inner = data.detail as { message?: string }
        if (typeof inner.message === 'string') return inner.message
      }
      if (data.message) return data.message
      if (data.error) return data.error
    }

    // HTTP status based messages
    const status = axiosError.response?.status
    if (status === 401) return 'Accesso richiesto: effettua il login.'
    if (status === 403) return 'Non hai i permessi per questa operazione.'
    if (status === 404) return 'Elemento non trovato.'
    if (status === 422) return 'Dati non validi: controlla i campi.'
    if (status === 429) return 'Troppe richieste: riprova tra poco.'
    if (status && status >= 500) return 'Errore del server: riprova tra poco.'

    // Network errors
    if (axiosError.code === 'ECONNABORTED') return 'La richiesta ha impiegato troppo: riprova.'
    if (!axiosError.response) return 'Server non raggiungibile: controlla la connessione.'

    return axiosError.message || 'Si è verificato un errore imprevisto'
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Si è verificato un errore imprevisto'
}

/**
 * Il corpo di `detail` quando e' un oggetto singolo (mai un array: la lista
 * di errori di validazione di FastAPI e' un `detail` array, e non e' quello
 * che `ApiError.raw` deve portare - un array non ha campi come
 * `articoli_non_prezzati` da leggere per nome).
 */
export function extractRawDetail(error: unknown): Record<string, unknown> | undefined {
  if (!axios.isAxiosError(error)) return undefined
  const data = error.response?.data as { detail?: unknown } | undefined
  const detail = data?.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail as Record<string, unknown>
  }
  return undefined
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// UPDATED: Request interceptor - add Bearer token or X-Auth-Token
apiClient.interceptors.request.use(
  async (config) => {
    // In bypass mode there is no real session: getAccessToken() would return
    // the placeholder 'bypass-token', and sending it as a Bearer header only
    // defeats the backend's localhost read bypass on routers that validate
    // the header. Return immediately with no credential at all — falling
    // through to the X-Auth-Token fallback below would attach a real token to
    // every verb, writes included, which the demo must never do.
    if (BYPASS_MODE) return config

    const authStore = useAuthStore()

    // Priority 1: User authentication (Bearer JWT token)
    try {
      const token = await authStore.getAccessToken()
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
        return config
      }
    } catch (error) {
      console.error('Failed to get access token:', error)
    }

    // Priority 2: Service-to-service fallback (X-Auth-Token)
    const apiToken = API_TOKEN
    if (apiToken) {
      config.headers['X-Auth-Token'] = apiToken
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// UPDATED: Response interceptor - handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
    const message = extractErrorMessage(error)
    const status = error.response?.status || 0

    // Handle 401 / 403 session expiration
    const isExpiredError = status === 401 || (status === 403 && (message.toLowerCase().includes('expired') || message.toLowerCase().includes('session')))
    if (isExpiredError && !originalRequest._retry) {
      originalRequest._retry = true

      const authStore = useAuthStore()

      // Check if this is a user auth request (has Authorization header)
      if (originalRequest.headers?.['Authorization']) {
        // Try to refresh token
        try {
          const token = await authStore.getAccessToken()
          if (token) {
            originalRequest.headers!['Authorization'] = `Bearer ${token}`
            return apiClient(originalRequest)
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
        }
      }

      // Refresh failed or service auth error - redirect to login
      if (!window.location.pathname.includes('/login')) {
        const currentPath = window.location.pathname + window.location.search
        sessionStorage.setItem('oauth_redirect', currentPath)
        await authStore.logout()
        window.location.href = `/login?error=session_expired&redirect=${encodeURIComponent(currentPath)}`
      }
    }

    // Show toast for errors (except 401 which redirects, and those the caller
    // shows itself)
    if (mostraToast(status, originalRequest?.silenzioso)) {
      ElMessage.error(message)
    }

    // Convert to ApiError for consistent handling. `detail` puo' essere un
    // oggetto (es. `{ message, articoli_non_prezzati: [...] }`): lo si porta
    // dietro cosi' com'e', il messaggio sopra e' gia' stato estratto.
    return Promise.reject(new ApiError(status, message, undefined, extractRawDetail(error)))
  }
)

// Typed API methods with retry logic
export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig, retries = 2): Promise<T> {
    try {
      const response = await apiClient.get<T>(url, config)
      return response.data
    } catch (error) {
      if (retries > 0 && axios.isAxiosError(error) && !error.response) {
        // Retry on network errors
        await new Promise(resolve => setTimeout(resolve, 1000))
        return this.get<T>(url, config, retries - 1)
      }
      throw error
    }
  },

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.post<T>(url, data, config)
    return response.data
  },

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.put<T>(url, data, config)
    return response.data
  },

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(url, data, config)
    return response.data
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(url, config)
    return response.data
  }
}

export default apiClient
